import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Header } from '../components/header';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/auth-context';
import { AlertCircle, ChevronUp, FileText, Flag, Info, Inspect, Users } from 'lucide-react';
import {
  Building,
  Shield,
  User,
  Globe,
  Phone,
  Award,
  AlertTriangle,
  Clock,
  Calendar,
  Check,
  ChevronDown,
  Star,
  MapPin,
} from 'lucide-react';
import { useTranslationWithLanguage } from '../context/language-context';
import { Card } from '@/components/ui/card';
import InspectionQuestionnairePage from './InspectionQuestionnairePage';

interface ExpandedSections {
  establishmentState: boolean;
  wps: boolean;
  workerInjury: boolean;
  employeesAbsconded: boolean;
  ownerDetails: boolean;
  workersDetails: boolean;
  nationalities: boolean;
  contactDetails: boolean;
  taqyeemDetails: boolean;
  riskReasons: boolean;
  previousVisits: boolean;
}

// Define Task interface based on the sample data
interface TaskItem {
  id: string;
  establishment_id: string;
  establishment_name_en: string;
  establishment_name_ar: string;
  establishment: {
    id: string;
    name_en: string;
    name_ar: string;
    license_no: string;
    license_expiry_date: string;
    license_issue_date: string;
    establishment_status_en: string;
    establishment_status_ar: string;
    sector_en: string | null;
    sector_ar: string | null;
    class_id: string;
    [key: string]: any;
  };
  [key: string]: any;
}

interface WPSPayment {
  allowances: string;
  amount: string;
  card_no: string;
  contract_salary: string;
  date_of_birth: string;
  eid_number: string;
  establishment_id: string;
  fixed_income: string;
  full_name_ar: string;
  person_name_en: string;
  id: string;
  paid_less: string;
  person_id: string;
  refund_amount: string;
  ymonth: string;
  [key: string]: any; // For additional fields
}

interface WorkerInjury {
  case_source: string | null;
  case_status: number | null;
  compensation_amount: string | null;
  date_of_birth: string;
  doctor_visited: boolean;
  eid_number: string | null;
  establishment_id: string;
  full_name_ar: string;
  person_name_en: string;
  id: string;
  incident_date: string;
  incident_description: string;
  incident_location_address: string | null;
  injury_part: string | null;
  person_id: string;
  [key: string]: any; // For additional fields
}

const EstablishmentAccordionPage = () => {
  const { id } = useParams<{ id: string }>();
  const { t, language } = useTranslationWithLanguage();
  const navigate = useNavigate();
  const { getAccessToken } = useAuth();

  // State for task data
  const [loading, setLoading] = useState<boolean>(true);
  const [currentTask, setCurrentTask] = useState<TaskItem | null>(
    null
  );
  const [questionnaireOpen, setQuestionnaireOpen] = useState<boolean>(false);
  const [wpsPayments, setWpsPayments] = useState<WPSPayment[]>([]);
  const [workerInjuries, setWorkerInjuries] = useState<
    WorkerInjury[]
  >([]);
  const [wpsCompliance, setWpsCompliance] = useState<{
    people_paid_percentage: number;
    wage_paid_percentage: string | number;
    paid_people: number;
    total_people: number;
    paid_wage_amount: string | number;
    total_wage_amount: string | number;
    percentage_payment?: number;
  } | null>(null);
  const [employeesAbsconded, setEmployeesAbsconded] = useState<any[]>(
    []
  );

  // State for data table
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedColumns, setSelectedColumns] = useState<{
    [key: string]: { [key: string]: boolean };
  }>({
    wps: {
      person_name_en: true,
      person_name_ar: true,
      compliance: false,
      paid: false,
      total_to_be_paid: false,
      percentage_payment: true,
      ymonth: true,
    },
    workerInjury: {
      full_name_en: true,
      incident_date: true,
      incident_description: true,
      doctor_visited: true,
      task_status_display: true,
      compensation_amount: true,
    },
  });

  // Pagination state
  const [pagination, setPagination] = useState({
    wps: {
      currentPage: 1,
      pageSize: 10,
      total: 0,
    },
    workerInjury: {
      currentPage: 1,
      pageSize: 10,
      total: 0,
    },
  });

  // State for expanded sections
  const [expandedSections, setExpandedSections] =
    useState<ExpandedSections>({
      establishmentState: true,
      wps: false,
      workerInjury: false,
      employeesAbsconded: false,
      ownerDetails: false,
      workersDetails: false,
      nationalities: false,
      contactDetails: false,
      taqyeemDetails: false,
      riskReasons: false,
      previousVisits: false,
    });

  // Toggle section expansion
  const toggleSection = (section: keyof ExpandedSections) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Expand all sections
  const expandAll = () => {
    const allExpanded = Object.keys(expandedSections).reduce(
      (acc, key) => {
        acc[key as keyof ExpandedSections] = true;
        return acc;
      },
      {} as ExpandedSections
    );

    setExpandedSections(allExpanded);
  };

  // Collapse all sections
  const collapseAll = () => {
    const allCollapsed = Object.keys(expandedSections).reduce(
      (acc, key) => {
        acc[key as keyof ExpandedSections] = false;
        return acc;
      },
      {} as ExpandedSections
    );

    setExpandedSections(allCollapsed);
  };

  // Get auth token from OIDC
  const getAuthToken = () => {
    return getAccessToken() || '';
  };


  // Fetch task data
  useEffect(() => {
    const fetchTaskData = async () => {
      if (!id) return;

      setLoading(true);
      try {
        const tid = localStorage.getItem('tid');

        // Fetch task details
        const taskUrl = `https://services-sdp-dev.mohre.gov.ae/jsonapi/transform/task_list?id=${tid}`;

        const headers = {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAccessToken()}`,
        };

        const taskResponse = await fetch(taskUrl, {
          headers,
        });

        if (taskResponse.ok) {
          const tasksData = await taskResponse.json();
          console.log('Tasks response:', tasksData);
          if (tasksData.status && tasksData.data) {
            // Set the first task as selected by default if available
            if (tasksData.data.length > 0) {
              setCurrentTask(tasksData.data[0]);
            }
            console.log('Tasks set:', tasksData.data);
          } else {
            console.error('No tasks data found in response');
          }
        } else {
          console.error(
            'Failed to fetch tasks:',
            taskResponse.status
          );
        }
      } catch (error) {
        console.error('Error fetching task data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTaskData();
  }, [id]);

  // Fetch WPS compliance data
  useEffect(() => {
    const fetchWpsCompliance = async () => {
      if (!id) return;
      localStorage.setItem('establishment_id', id);

      try {
        const headers = {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAccessToken()}`,
        };

        const wpsCondition = JSON.stringify({
          rules: [
            { field: 'establishment_id', operator: 'eq', value: id },
          ],
        });

        const wpsResponse = await fetch(
          `https://services-sdp-dev.mohre.gov.ae/sdp/api/v1/vw_WPSEstablishmentMetrics?condition=${encodeURIComponent(
            wpsCondition
          )}`,
          { headers }
        );

        if (wpsResponse.ok) {
          const wpsData = await wpsResponse.json();
          console.log('WPS establishment metrics response:', wpsData);
          if (
            wpsData.success &&
            wpsData.data &&
            wpsData.data.length > 0
          ) {
            // Format metrics from the new API to match the expected structure
            const metrics = wpsData.data[0];
            // Calculate derived values for the UI components
            const formattedData = {
              total_people:
                metrics.total_compliant +
                metrics.total_non_compliant +
                metrics.total_exempt,
              paid_people:
                metrics.total_compliant + metrics.total_exempt,
              people_paid_percentage: parseFloat(
                metrics.percentage_compliant
              ),
              total_wage_amount: parseFloat(metrics.total_to_be_paid),
              paid_wage_amount: parseFloat(metrics.total_paid),
              wage_paid_percentage: parseFloat(
                metrics.percentage_paid
              ),
              // Calculate percentage_payment from the available data if needed
              percentage_payment: parseFloat(metrics.percentage_paid), // Use percentage_paid as fallback
            };
            setWpsCompliance(formattedData);
            console.log('WPS compliance set:', formattedData);
          } else {
            console.error('No WPS compliance data found in response');
          }
        } else {
          console.error(
            'Failed to fetch WPS compliance:',
            wpsResponse.status
          );
        }
      } catch (error) {
        console.error('Error fetching WPS compliance:', error);
      }
    };

    fetchWpsCompliance();
  }, [id]);

  // Fetch employees absconded data
  useEffect(() => {
    const fetchEmployeesAbsconded = async () => {
      if (!id) return;

      try {
        const headers = {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAccessToken()}`,
        };

        const condition = JSON.stringify({
          rules: [
            { field: 'person_status_id', operator: 'eq', value: '3' },
          ],
        });

        const response = await fetch(
          `https://services-sdp-dev.mohre.gov.ae/sdp/api/v1/person?condition=${encodeURIComponent(
            condition
          )}`,
          { headers }
        );

        if (response.ok) {
          const data = await response.json();
          console.log('Employees absconded response:', data);
          if (data.success && data.data) {
            setEmployeesAbsconded(data.data);
            console.log('Employees absconded set:', data.data);
          } else {
            console.error(
              'No employees absconded data found in response'
            );
          }
        } else {
          console.error(
            'Failed to fetch employees absconded:',
            response.status
          );
        }
      } catch (error) {
        console.error('Error fetching employees absconded:', error);
      }
    };

    fetchEmployeesAbsconded();
  }, [id]);

  // Fetch WPS payments with pagination and search
  useEffect(() => {
    const fetchWpsPayments = async () => {
      if (!id) return;

      try {
        const headers = {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAccessToken()}`,
        };

        const wpsCondition = JSON.stringify({
          rules: [
            { field: 'establishment_id', operator: 'eq', value: id },
          ],
        });

        // Create search filter if search term exists
        let searchParams = '';
        if (searchTerm) {
          const searchFields = Object.keys(
            selectedColumns.wps
          ).filter((key) => selectedColumns.wps[key]);

          const globalFilter = JSON.stringify({
            searchTerm,
            searchFields: searchFields,
          });

          searchParams = `&globalFilter=${encodeURIComponent(
            globalFilter
          )}`;
        }

        const wpsResponse = await fetch(
          `https://services-sdp-dev.mohre.gov.ae/sdp/api/v1/vw_WPSCompliance?condition=${encodeURIComponent(
            wpsCondition
          )}&page=${pagination.wps.currentPage}&pageSize=${
            pagination.wps.pageSize
          }${searchParams}`,
          { headers }
        );
        if (wpsResponse.ok) {
          const wpsData = await wpsResponse.json();
          console.log('WPS compliance details response:', wpsData);
          if (wpsData.success && wpsData.data) {
            setWpsPayments(wpsData.data);
            setPagination((prev) => ({
              ...prev,
              wps: {
                ...prev.wps,
                total: wpsData.total || wpsData.data.length,
              },
            }));
            console.log('WPS payments set:', wpsData.data);
          } else {
            console.error('No WPS payments data found in response');
          }
        } else {
          console.error(
            'Failed to fetch WPS payments:',
            wpsResponse.status
          );
        }
      } catch (error) {
        console.error('Error fetching WPS payments:', error);
      }
    };

    fetchWpsPayments();
  }, [
    id,
    pagination.wps.currentPage,
    pagination.wps.pageSize,
    searchTerm,
  ]);

  // Fetch worker injuries with pagination and search
  useEffect(() => {
    const fetchWorkerInjuries = async () => {
      if (!id) return;

      try {
        const headers = {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAccessToken()}`,
        };

        const condition = {
          rules: [
            {
              field: "establishment_id",
              operator: "eq",
              value: id,
            }
          ]
        };
        const encodedCondition = encodeURIComponent(JSON.stringify(condition));
        const injuryResponse = await fetch(
          `https://services-sdp-dev.mohre.gov.ae/jsonapi/transform/injury_list?` +
          `page=${pagination.workerInjury.currentPage}&pageSize=${pagination.workerInjury.pageSize}` +
          `&condition=${encodedCondition}`,
          { headers }
        );
        if (injuryResponse.ok) {
          const injuryData = await injuryResponse.json();
          console.log('Worker injuries response:', injuryData);
          if (injuryData.status && injuryData.data) {
            injuryData.data.forEach((injury: WorkerInjury) => {
              injury.insurance_company = 'Dubai Insurance';
              // Add 10 days to incident date
              const incidentDate = new Date(
                injury.params?.incident_date
                  ? injury.params?.incident_date
                  : '2025-04-06'
              );
              const claimDate = new Date(
                incidentDate.setDate(incidentDate.getDate() + 10)
              )
                .toISOString()
                .split('T')[0];
              injury.claim_date = claimDate;
              injury.comm_date = injury.params?.incident_date;
            });
            setWorkerInjuries(injuryData.data);
            setPagination((prev) => ({
              ...prev,
              workerInjury: {
                ...prev.workerInjury,
                total: injuryData.total || injuryData.data.length,
              },
            }));
            console.log('Worker injuries set:', injuryData.data);
          } else {
            console.error(
              'No worker injuries data found in response'
            );
          }
        } else {
          console.error(
            'Failed to fetch worker injuries:',
            injuryResponse.status
          );
        }
      } catch (error) {
        console.error('Error fetching worker injuries:', error);
      }
    };

    fetchWorkerInjuries();
  }, [
    id,
    pagination.workerInjury.currentPage,
    pagination.workerInjury.pageSize,
    searchTerm,
  ]);

  // Format date for display
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      // Check if date is valid
      if (isNaN(date.getTime())) return '-';
      return date.toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" })
      // return date.toISOString().split('T')[0]; // Returns YYYY-MM-DD format
    } catch (error) {
      console.error('Error formatting date:', error);
      return '-';
    }
  };

  // Format ymonth (format: 202502) to display as "Feb 2025"
  const formatYMonth = (ymonthString: string | null | undefined) => {
    if (!ymonthString) return '-';
    try {
      let year: string;
      let month: number;
      // Check if format is YYYY-MM (from the new API)
      if (ymonthString.includes('-')) {
        const parts = ymonthString.split('-');
        year = parts[0];
        month = parseInt(parts[1], 10);
      }
      // Check if format is YYYYMM (from the old API)
      else if (ymonthString.length === 6) {
        year = ymonthString.substring(0, 4);
        month = parseInt(ymonthString.substring(4, 6), 10);
      } else {
        return '-';
      }

      const date = new Date(parseInt(year), month - 1, 1);
      if (isNaN(date.getTime())) return '-';

      const monthName = date.toLocaleString('en-US', {
        month: 'short',
      });
      return `${monthName} ${year}`;
    } catch (error) {
      console.error('Error formatting ymonth:', error);
      return '-';
    }
  };

  // Format incident_date to local date string
  const formatIncidentDate = (
    dateString: string | null | undefined
  ) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      // Check if date is valid
      if (isNaN(date.getTime())) return '-';
      return date.toLocaleDateString();
    } catch (error) {
      console.error('Error formatting incident date:', error);
      return '-';
    }
  };

  // Mask card number to show only first two digits
  const maskCardNumber = (cardNumber: string | null | undefined) => {
    if (!cardNumber) return '-';
    if (cardNumber.length < 3) return cardNumber;
    return (
      cardNumber.substring(0, 2) + 'X'.repeat(cardNumber.length - 2)
    );
  };

  // Mask EID number to show only last 4 digits
  const maskEidNumber = (eidNumber: string | null | undefined) => {
    if (!eidNumber) return '-';
    if (eidNumber.length <= 4) return eidNumber;
    return 'X'.repeat(eidNumber.length - 4) + eidNumber.slice(-4);
  };

  // Log the task data for debugging
  useEffect(() => {
    if (currentTask) {
      console.log('Current task data:', currentTask);
    }
  }, [currentTask]);

  // Helper function to get the best available value from multiple possible field names
  const getFieldValue = (
    obj: any,
    fieldNames: string[],
    defaultValue: string = '-'
  ) => {
    for (const field of fieldNames) {
      if (
        obj[field] &&
        obj[field] !== '-' &&
        obj[field] !== 'null' &&
        obj[field] !== 'undefined'
      ) {
        return obj[field];
      }
    }
    return defaultValue;
  };

  // Use establishment data from task or fallback to mock data
  const establishment = currentTask?.establishment || {
    id: id,
    name_en: 'Loading...',
    name_ar: 'جاري التحميل...',
    establishment_name_en: 'Loading...',
    establishment_name_ar: 'جاري التحميل...',
    license_number: '-',
    license_no: '-',
    license_expiry_date: '-',
    expiry_date: '-',
    registration_date: '-',
    reg_date: '-',
    category: '-',
    category_name_en: '-',
    sector_en: '-',
    sector_ar: '-',
    sector_name_en: '-',
    sector_name_ar: '-',
    status: 'Active',
    status_name_en: 'Active',
    last_updated: '-',
    risk_grade: '-',
    risk_level: '-',
  };

  // Extract risk score from subject or incident description
  const extractRiskScore = () => {
    const subject = currentTask?.params?.subject || ""
    const match = subject.match(/Score: (\d+\.\d+)/)
    return match ? match[1] : "N/A"
  }

  return (
    <div
      className="flex flex-col pb-16"
      dir={language === 'ar' ? 'rtl' : 'ltr'}
    >
            {/* Establishment Details Sections */}
        <div className="bg-white rounded-lg w-full overflow-hidden border border-gold-200 shadow-md">
          {/* Conditional Expand/Collapse All Button */}
          <div className="flex justify-end p-2 border-b border-gold-200">
            {Object.values(expandedSections).some(
              (value) => value
            ) ? (
              <button
                onClick={collapseAll}
                className="text-[#966f2e] text-sm flex items-center"
              >
                <ChevronDown className="w-4 h-4 mr-1" />
                {t('establishment.collapseAll')}
              </button>
            ) : (
              <button
                onClick={expandAll}
                className="text-[#966f2e] text-sm flex items-center"
              >
                <ChevronDown className="w-4 h-4 mr-1 rotate-180" />
                {t('establishment.expandAll')}
              </button>
            )}
          </div>
          <div className="divide-y divide-gray-200">
            {/* Establishment State */}
            {/* <div>
              <button
                className="w-full flex items-center justify-between p-4 text-left"
                onClick={() => toggleSection('establishmentState')}
              >
                <div className="flex items-center">
                  <Building
                    className={`w-5 h-5 ${
                      language === 'ar' ? 'ml-3' : 'mr-3'
                    } text-[#966f2e]`}
                  />
                  <div>
                    <div>{t('establishment.establishmentState')}</div>
                    <div className="text-xs text-gray-500">
                      {t('establishment.licenseInfo')}
                    </div>
                  </div>
                </div>
                <ChevronDown
                  className={`w-5 h-5 text-gray-400 transition-transform ${
                    expandedSections.establishmentState
                      ? 'rotate-180'
                      : ''
                  }`}
                />
              </button>

              {expandedSections.establishmentState && (
                <div className="p-4 bg-gray-50">
                  <div className="flex items-center mb-4">
                    <span className="bg-green-100 text-green-800 text-xs font-medium px-3 py-1 rounded-full">
                      {establishment.status ||
                        (language === 'en' ? 'Active' : 'نشط')}
                    </span>
                    <span className="ml-3 text-sm text-gray-500">
                      {language === 'en'
                        ? `Last updated: ${
                            establishment.last_updated || '-'
                          }`
                        : `آخر تحديث: ${
                            establishment.last_updated || '-'
                          }`}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-sm text-gray-500">
                        {language === 'en'
                          ? 'License Number'
                          : 'رقم الترخيص'}
                      </p>
                      <p className="font-medium">
                        {getFieldValue(establishment, [
                          'license_number',
                          'license_no',
                        ])}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">
                        {language === 'en'
                          ? 'Expiry Date'
                          : 'تاريخ الانتهاء'}
                      </p>
                      <p className="font-medium">
                        {formatDate(
                          getFieldValue(establishment, [
                            'license_expiry_date',
                            'expiry_date',
                          ])
                        )}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-sm text-gray-500">
                        {language === 'en'
                          ? 'Registration Date'
                          : 'تاريخ التسجيل'}
                      </p>
                      <p className="font-medium">
                        {formatDate(
                          getFieldValue(establishment, [
                            'registration_date',
                            'reg_date',
                          ])
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">
                        {language === 'en' ? 'Category' : 'الفئة'}
                      </p>
                      <p className="font-medium">
                        {getFieldValue(establishment, [
                          'category',
                          'category_name_en',
                        ])}
                      </p>
                    </div>
                  </div>

                  <div>
                    <p className="text-sm text-gray-500">
                      {language === 'en' ? 'Sector' : 'القطاع'}
                    </p>
                    <p className="font-medium">
                      {language === 'en'
                        ? getFieldValue(establishment, [
                            'sector_en',
                            'sector_name_en',
                          ])
                        : getFieldValue(establishment, [
                            'sector_ar',
                            'sector_name_ar',
                          ])}
                    </p>
                  </div>
                </div>
              )}
            </div> */}

            {/* WPS */}
            <div>
              <button
                className="w-full flex items-center justify-between p-4 text-left"
                onClick={() => toggleSection('wps')}
              >
                <div className="flex items-center">
                  <Shield
                    className={`w-5 h-5 ${
                      language === 'ar' ? 'ml-3' : 'mr-3'
                    } text-[#966f2e]`}
                  />
                  <div>
                    <div>
                      {language === 'en'
                        ? 'WPS (Wage Protection System)'
                        : 'نظام حماية الأجور'}
                    </div>
                    <div className="text-xs text-gray-500">
                      {language === 'en'
                        ? 'Salary payment compliance and history'
                        : 'الامتثال لدفع الرواتب والتاريخ'}
                    </div>
                  </div>
                </div>
                <ChevronDown
                  className={`w-5 h-5 text-gray-400 transition-transform ${
                    expandedSections.wps ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {expandedSections.wps && (
                <div className="p-4 bg-gray-50">
                  <div className="flex items-center mb-4">
                    <span className="bg-green-100 text-green-800 text-xs font-medium px-3 py-1 rounded-full">
                      {language === 'en' ? 'Compliant' : 'ملتزم'}
                    </span>
                    <span className="ml-3 text-sm text-gray-500">
                      {language === 'en'
                        ? `Total Pending Payments: ${pagination.wps.total}`
                        : `مدفوعات الانحناء الإجمالية: ${pagination.wps.total}`}
                    </span>
                  </div>

                  {/* WPS Compliance Cards */}
                  {wpsCompliance && (
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      {/* People Paid Percentage Card */}
                      <div
                        className={`p-3 rounded-lg shadow-sm ${
                          wpsCompliance.people_paid_percentage >= 80
                            ? 'bg-[#b8925622] border border-green-100'
                            : 'bg-red-50 border border-red-100'
                        }`}
                      >
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="text-sm font-medium">
                              {language === 'en'
                                ? 'People Compliance'
                                : 'الأشخاص المدفوعين'}
                            </p>
                            <p className="text-xs text-gray-500">
                              {wpsCompliance.paid_people} /{' '}
                              {wpsCompliance.total_people}
                            </p>
                          </div>
                          <div
                            className={`text-xl font-bold ${
                              wpsCompliance.people_paid_percentage >=
                              80
                                ? 'text-[#b89256]'
                                : 'text-red-600'
                            }`}
                          >
                            {wpsCompliance.people_paid_percentage.toFixed(
                              1
                            )}
                            %
                          </div>
                        </div>
                        <div className="mt-2 w-full bg-gray-200 rounded-full h-1.5">
                          <div
                            className={`h-1.5 rounded-full ${
                              wpsCompliance.people_paid_percentage >=
                              80
                                ? 'bg-[#b89256]'
                                : 'bg-red-500'
                            }`}
                            style={{
                              width: `${Math.min(
                                100,
                                wpsCompliance.people_paid_percentage
                              )}%`,
                            }}
                          ></div>
                        </div>
                        <div className="mt-1 text-xs text-right hidden">
                          {wpsCompliance.people_paid_percentage >= 80
                            ? language === 'en'
                              ? 'Compliant'
                              : 'ملتزم'
                            : language === 'en'
                            ? 'Non-Compliant'
                            : 'غير ملتزم'}
                        </div>
                      </div>

                      {/* Wage Paid Percentage Card */}
                      <div
                        className={`p-3 rounded-lg shadow-sm ${
                          parseFloat(
                            wpsCompliance.wage_paid_percentage
                          ) >= 80
                            ? 'bg-[#b8925622] border border-green-100'
                            : 'bg-red-50 border border-red-100'
                        }`}
                      >
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="text-sm font-medium">
                              {language === 'en'
                                ? 'Wage Compliance'
                                : 'الأجور المدفوعة'}
                            </p>
                            <p className="text-xs text-[gray-500]">
                              {parseFloat(
                                wpsCompliance.paid_wage_amount
                              ).toLocaleString()}{' '}
                              /{' '}
                              {parseFloat(
                                wpsCompliance.total_wage_amount
                              ).toLocaleString()}
                            </p>
                          </div>
                          <div
                            className={`text-xl font-bold ${
                              parseFloat(
                                wpsCompliance.wage_paid_percentage
                              ) >= 80
                                ? 'text-[#b89256]'
                                : 'text-red-600'
                            }`}
                          >
                            {parseFloat(
                              wpsCompliance.wage_paid_percentage
                            ).toFixed(1)}
                            %
                          </div>
                        </div>
                        <div className="mt-2 w-full bg-gray-200 rounded-full h-1.5">
                          <div
                            className={`h-1.5 rounded-full ${
                              parseFloat(
                                wpsCompliance.wage_paid_percentage
                              ) >= 80
                                ? 'bg-[#b89256]'
                                : 'bg-red-500'
                            }`}
                            style={{
                              width: `${Math.min(
                                100,
                                parseFloat(
                                  wpsCompliance.wage_paid_percentage
                                )
                              )}%`,
                            }}
                          ></div>
                        </div>
                        <div className="mt-1 text-xs text-right hidden">
                          {parseFloat(
                            wpsCompliance.wage_paid_percentage
                          ) >= 80
                            ? language === 'en'
                              ? 'Compliant'
                              : 'ملتزم'
                            : language === 'en'
                            ? 'Non-Compliant'
                            : 'غير ملتزم'}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Search and Column Selection */}
                  <div className="mb-4 flex flex-wrap gap-2 items-center">
                    <div className="flex-1">
                      <input
                        type="text"
                        placeholder={
                          language === 'en'
                            ? 'Search payments...'
                            : 'البحث في المدفوعات...'
                        }
                        value={searchTerm}
                        onChange={(e) => {
                          setSearchTerm(e.target.value);
                          // Reset to first page when searching
                          setPagination((prev) => ({
                            ...prev,
                            wps: {
                              ...prev.wps,
                              currentPage: 1,
                            },
                          }));
                        }}
                        className="w-full p-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                    <div className="relative">
                      <button
                        className="px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg flex items-center text-sm"
                        onClick={() => {
                          const dropdown = document.getElementById(
                            'wps-column-dropdown'
                          );
                          if (dropdown)
                            dropdown.classList.toggle('hidden');
                        }}
                      >
                        {language === 'en' ? 'Columns' : 'الأعمدة'}
                        <ChevronDown className="ml-1 h-4 w-4" />
                      </button>
                      <div
                        id="wps-column-dropdown"
                        className="hidden absolute right-0 mt-1 bg-white border border-gold-200 rounded-lg shadow-lg z-10 p-2 w-48"
                      >
                        {[
                          'person_name_en',
                          'person_name_ar',
                          'paid',
                          'total_to_be_paid',
                          'percentage_payment',
                          'ymonth',
                        ].map((col) => (
                          <div
                            key={col}
                            className="flex items-center p-1"
                          >
                            <input
                              type="checkbox"
                              id={`wps-col-${col}`}
                              checked={selectedColumns.wps[col]}
                              onChange={() => {
                                setSelectedColumns((prev) => ({
                                  ...prev,
                                  wps: {
                                    ...prev.wps,
                                    [col]: !prev.wps[col],
                                  },
                                }));
                              }}
                              className="mr-2"
                            />
                            <label
                              htmlFor={`wps-col-${col}`}
                              className="text-sm"
                            >
                              {col.replace(/_/g, ' ')}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* WPS Compliance Details Table */}
                  <div className="overflow-x-auto">
                    <table className="min-w-full bg-white border border-gold-200 rounded-lg">
                      <thead className="bg-gray-100">
                        <tr>
                          {Object.entries(selectedColumns.wps).map(
                            ([col, visible]) =>
                              visible && (
                                <th
                                  key={col}
                                  className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                >
                                  {col.replace(/_/g, ' ')}
                                </th>
                              )
                          )}
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {language === 'en' ? 'Status' : 'الحالة'}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {wpsPayments
                          .slice(
                            (pagination.wps.currentPage - 1) *
                              pagination.wps.pageSize,
                            pagination.wps.currentPage *
                              pagination.wps.pageSize
                          )
                          .filter((payment) => {
                            if (!searchTerm) return true;
                            // Search in key fields
                            return (
                              (payment.person_name_en &&
                                payment.person_name_en
                                  .toLowerCase()
                                  .includes(
                                    searchTerm.toLowerCase()
                                  )) ||
                              (payment.person_name_ar &&
                                payment.person_name_ar
                                  .toLowerCase()
                                  .includes(
                                    searchTerm.toLowerCase()
                                  )) ||
                              (payment.compliance &&
                                payment.compliance
                                  .toLowerCase()
                                  .includes(searchTerm.toLowerCase()))
                            );
                          })
                          .map((payment, index) => (
                            <tr
                              key={payment.id || index}
                              className={
                                index % 2 === 0
                                  ? 'bg-white'
                                  : 'bg-gray-50'
                              }
                            >
                              {Object.entries(
                                selectedColumns.wps
                              ).map(([col, visible]) => {
                                if (!visible) return null;

                                let value = '-';

                                if (col === 'ymonth') {
                                  // Format ymonth to display as "Feb 2025"
                                  value = formatYMonth(payment[col]);
                                } else if (
                                  col === 'paid' ||
                                  col === 'total_to_be_paid'
                                ) {
                                  // Format currency values
                                  value = parseFloat(
                                    payment[col] || 0
                                  ).toLocaleString();
                                } else if (
                                  col === 'percentage_payment'
                                ) {
                                  // Access the percentage_paid from the nested structure
                                  const percentagePaid =
                                    payment.params?.result?.compliant
                                      ?.percentage_paid;
                                  value = percentagePaid
                                    ? percentagePaid.toFixed(1) + '%'
                                    : '-';
                                } else {
                                  // Default handling for other columns
                                  value = payment[col] || '-';
                                }

                                return (
                                  <td
                                    key={col}
                                    className="px-4 py-2 text-sm"
                                  >
                                    {value}
                                  </td>
                                );
                              })}
                              <td className="px-4 py-2">
                                <span
                                  className={`px-2 py-1 text-xs rounded-full ${
                                    payment.compliance ===
                                      'compliant' ||
                                    payment.params?.result?.compliant
                                      ?.percentage_paid >= 80 ||
                                    payment.compliance === 'exempt'
                                      ? 'bg-green-100 text-green-800'
                                      : 'bg-red-100 text-red-800'
                                  }`}
                                >
                                  {payment.compliance === 'exempt'
                                    ? language === 'en'
                                      ? 'Exempt'
                                      : 'معفى'
                                    : payment.compliance ===
                                        'compliant' ||
                                      payment.params?.result
                                        ?.compliant
                                        ?.percentage_paid >= 80
                                    ? language === 'en'
                                      ? 'Compliant'
                                      : 'ملتزم'
                                    : language === 'en'
                                    ? 'Non-Compliant'
                                    : 'غير ملتزم'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        {wpsPayments.length === 0 && (
                          <tr>
                            <td
                              colSpan={
                                Object.values(
                                  selectedColumns.wps
                                ).filter(Boolean).length + 1
                              }
                              className="px-4 py-4 text-center text-gray-500"
                            >
                              {language === 'en'
                                ? 'No compliance data available'
                                : 'لا توجد بيانات امتثال متاحة'}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>

                    {/* Pagination Controls for WPS */}
                    {wpsPayments.length > 0 && (
                      <div className="flex justify-between items-center mt-4">
                        <div className="text-sm text-gray-500">
                          {language === 'en'
                            ? `Showing ${Math.min(
                                (pagination.wps.currentPage - 1) *
                                  pagination.wps.pageSize +
                                  1,
                                pagination.wps.total
                              )} to ${Math.min(
                                pagination.wps.currentPage *
                                  pagination.wps.pageSize,
                                pagination.wps.total
                              )} of ${pagination.wps.total} entries`
                            : `عرض ${Math.min(
                                (pagination.wps.currentPage - 1) *
                                  pagination.wps.pageSize +
                                  1,
                                pagination.wps.total
                              )} إلى ${Math.min(
                                pagination.wps.currentPage *
                                  pagination.wps.pageSize,
                                pagination.wps.total
                              )} من ${pagination.wps.total} سجل`}
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() =>
                              setPagination((prev) => ({
                                ...prev,
                                wps: {
                                  ...prev.wps,
                                  currentPage: Math.max(
                                    1,
                                    prev.wps.currentPage - 1
                                  ),
                                },
                              }))
                            }
                            disabled={
                              pagination.wps.currentPage === 1
                            }
                            className={`px-3 py-1 rounded border ${
                              pagination.wps.currentPage === 1
                                ? 'bg-gray-100 text-gray-400 border-gold-200'
                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {language === 'en'
                              ? 'Previous'
                              : 'السابق'}
                          </button>
                          <button
                            onClick={() =>
                              setPagination((prev) => ({
                                ...prev,
                                wps: {
                                  ...prev.wps,
                                  currentPage: Math.min(
                                    Math.ceil(
                                      pagination.wps.total /
                                        prev.wps.pageSize
                                    ),
                                    prev.wps.currentPage + 1
                                  ),
                                },
                              }))
                            }
                            disabled={
                              pagination.wps.currentPage >=
                              Math.ceil(
                                pagination.wps.total /
                                  pagination.wps.pageSize
                              )
                            }
                            className={`px-3 py-1 rounded border ${
                              pagination.wps.currentPage >=
                              Math.ceil(
                                pagination.wps.total /
                                  pagination.wps.pageSize
                              )
                                ? 'bg-gray-100 text-gray-400 border-gold-200'
                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {language === 'en' ? 'Next' : 'التالي'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Worker Injury */}
            <div>
              <button
                className="w-full flex items-center justify-between p-4 text-left"
                onClick={() => toggleSection('workerInjury')}
              >
                <div className="flex items-center">
                  <AlertTriangle
                    className={`w-5 h-5 ${
                      language === 'ar' ? 'ml-3' : 'mr-3'
                    } text-[#966f2e]`}
                  />
                  <div>
                    <div>
                      {language === 'en'
                        ? 'Worker Injury'
                        : 'إصابات العمال'}
                    </div>
                    <div className="text-xs text-gray-500">
                      {language === 'en'
                        ? 'Worker injury reports and details'
                        : 'تقارير وتفاصيل إصابات العمال'}
                    </div>
                  </div>
                </div>
                <ChevronDown
                  className={`w-5 h-5 text-gray-400 transition-transform ${
                    expandedSections.workerInjury ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {expandedSections.workerInjury && (
                <div className="p-4 bg-gray-50">
                  <div className="flex items-center mb-4">
                    <span className="bg-amber-100 text-amber-800 text-xs font-medium px-3 py-1 rounded-full">
                      {language === 'en'
                        ? 'Injury Reports'
                        : 'تقارير الإصابات'}
                    </span>
                    <span className="ml-3 text-sm text-gray-500">
                      {language === 'en'
                        ? `Total Reports: ${pagination.workerInjury.total}`
                        : `إجمالي التقارير: ${pagination.workerInjury.total}`}
                    </span>
                  </div>

                  {/* Search and Column Selection */}
                  <div className="mb-4 flex flex-wrap gap-2 items-center">
                    <div className="flex-1">
                      <input
                        type="text"
                        placeholder={
                          language === 'en'
                            ? 'Search injuries...'
                            : 'البحث في الإصابات...'
                        }
                        value={searchTerm}
                        onChange={(e) => {
                          setSearchTerm(e.target.value);
                          // Reset to first page when searching
                          setPagination((prev) => ({
                            ...prev,
                            workerInjury: {
                              ...prev.workerInjury,
                              currentPage: 1,
                            },
                          }));
                        }}
                        className="w-full p-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                    <div className="relative">
                      <button
                        className="px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg flex items-center text-sm"
                        onClick={() => {
                          const dropdown = document.getElementById(
                            'injury-column-dropdown'
                          );
                          if (dropdown)
                            dropdown.classList.toggle('hidden');
                        }}
                      >
                        {language === 'en' ? 'Columns' : 'الأعمدة'}
                        <ChevronDown className="ml-1 h-4 w-4" />
                      </button>
                      <div
                        id="injury-column-dropdown"
                        className="hidden absolute right-0 mt-1 bg-white border border-gold-200 rounded-lg shadow-lg z-10 p-2 w-48"
                      >
                        {[
                          'person_name_en',
                          'incident_date',
                          'incident_description',
                          'insurance_company',
                          'claim_date',
                          'comm_date',
                          'doctor_visited',
                          'compensation_amount',
                        ].map((col) => (
                          <div
                            key={col}
                            className="flex items-center p-1"
                          >
                            <input
                              type="checkbox"
                              id={`injury-col-${col}`}
                              checked={
                                selectedColumns.workerInjury[col]
                              }
                              onChange={() => {
                                setSelectedColumns((prev) => ({
                                  ...prev,
                                  workerInjury: {
                                    ...prev.workerInjury,
                                    [col]: !prev.workerInjury[col],
                                  },
                                }));
                              }}
                              className="mr-2"
                            />
                            <label
                              htmlFor={`injury-col-${col}`}
                              className="text-sm"
                            >
                              {col.replace(/_/g, ' ')}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Worker Injuries Table */}
                  <div className="overflow-x-auto">
                    <table className="min-w-full bg-white border border-gold-200 rounded-lg">
                      <thead className="bg-gray-100">
                        <tr>
                          {Object.entries(
                            selectedColumns.workerInjury
                          ).map(
                            ([col, visible]) =>
                              visible && (
                                <th
                                  key={col}
                                  className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                >
                                  {col.replace(/_/g, ' ')}
                                </th>
                              )
                          )}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {workerInjuries
                          .filter((injury) => {
                            if (!searchTerm) return true;
                            // Custom search for the new structure
                            return (
                              (injury.person?.full_name_en &&
                                injury.person.full_name_en
                                  .toLowerCase()
                                  .includes(
                                    searchTerm.toLowerCase()
                                  )) ||
                              (injury.params?.incident_description &&
                                injury.params.incident_description
                                  .toLowerCase()
                                  .includes(
                                    searchTerm.toLowerCase()
                                  )) ||
                              (injury.task_status_display &&
                                injury.task_status_display
                                  .toLowerCase()
                                  .includes(searchTerm.toLowerCase()))
                            );
                          })
                          .map((injury, index) => (
                            <tr
                              key={injury.id || index}
                              className={
                                index % 2 === 0
                                  ? 'bg-white'
                                  : 'bg-gray-50'
                              }
                            >
                              {Object.entries(
                                selectedColumns.workerInjury
                              ).map(([col, visible]) => {
                                if (!visible) return null;
                                let value = '';
                                // Map fields from the new structure
                                if (col === 'full_name_en') {
                                  value =
                                    injury.person?.full_name_en ||
                                    '-';
                                } else if (col === 'incident_date') {
                                  value =
                                    formatIncidentDate(
                                      injury.params?.incident_date
                                    ) || '-';
                                } else if (
                                  col === 'incident_description'
                                ) {
                                  value =
                                    injury.params
                                      ?.incident_description || '-';
                                } else if (col === 'doctor_visited') {
                                  value = injury.params
                                    ?.doctor_visited
                                    ? language === 'en'
                                      ? 'Yes'
                                      : 'نعم'
                                    : language === 'en'
                                    ? 'No'
                                    : 'لا';
                                } else if (
                                  col === 'task_status_display'
                                ) {
                                  value =
                                    injury.task_status_display || '-';
                                }
                                return (
                                  <td
                                    key={col}
                                    className="px-4 py-2 text-sm"
                                  >
                                    {value}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        {workerInjuries.length === 0 && (
                          <tr>
                            <td
                              colSpan={
                                Object.values(
                                  selectedColumns.workerInjury
                                ).filter(Boolean).length
                              }
                              className="px-4 py-4 text-center text-gray-500"
                            >
                              {language === 'en'
                                ? 'No injury data available'
                                : 'لا توجد بيانات إصابات متاحة'}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>

                    {/* Pagination Controls for Worker Injuries */}
                    {workerInjuries.length > 0 && (
                      <div className="flex justify-between items-center mt-4">
                        <div className="text-sm text-gray-500">
                          {language === 'en'
                            ? `Showing ${Math.min(
                                (pagination.workerInjury.currentPage -
                                  1) *
                                  pagination.workerInjury.pageSize +
                                  1,
                                pagination.workerInjury.total
                              )} to ${Math.min(
                                pagination.workerInjury.currentPage *
                                  pagination.workerInjury.pageSize,
                                pagination.workerInjury.total
                              )} of ${
                                pagination.workerInjury.total
                              } entries`
                            : `عرض ${Math.min(
                                (pagination.workerInjury.currentPage -
                                  1) *
                                  pagination.workerInjury.pageSize +
                                  1,
                                pagination.workerInjury.total
                              )} إلى ${Math.min(
                                pagination.workerInjury.currentPage *
                                  pagination.workerInjury.pageSize,
                                pagination.workerInjury.total
                              )} من ${
                                pagination.workerInjury.total
                              } سجل`}
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() =>
                              setPagination((prev) => ({
                                ...prev,
                                workerInjury: {
                                  ...prev.workerInjury,
                                  currentPage: Math.max(
                                    1,
                                    prev.workerInjury.currentPage - 1
                                  ),
                                },
                              }))
                            }
                            disabled={
                              pagination.workerInjury.currentPage ===
                              1
                            }
                            className={`px-3 py-1 rounded border ${
                              pagination.workerInjury.currentPage ===
                              1
                                ? 'bg-gray-100 text-gray-400 border-gold-200'
                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {language === 'en'
                              ? 'Previous'
                              : 'السابق'}
                          </button>
                          <button
                            onClick={() =>
                              setPagination((prev) => ({
                                ...prev,
                                workerInjury: {
                                  ...prev.workerInjury,
                                  currentPage: Math.min(
                                    Math.ceil(
                                      pagination.workerInjury.total /
                                        prev.workerInjury.pageSize
                                    ),
                                    prev.workerInjury.currentPage + 1
                                  ),
                                },
                              }))
                            }
                            disabled={
                              pagination.workerInjury.currentPage >=
                              Math.ceil(
                                pagination.workerInjury.total /
                                  pagination.workerInjury.pageSize
                              )
                            }
                            className={`px-3 py-1 rounded border ${
                              pagination.workerInjury.currentPage >=
                              Math.ceil(
                                pagination.workerInjury.total /
                                  pagination.workerInjury.pageSize
                              )
                                ? 'bg-gray-100 text-gray-400 border-gold-200'
                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {language === 'en' ? 'Next' : 'التالي'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Employees Absconded */}
            <div>
              <button
                className="w-full flex items-center justify-between p-4 text-left"
                onClick={() => toggleSection('employeesAbsconded')}
              >
                <div className="flex items-center">
                  <AlertTriangle
                    className={`w-5 h-5 ${
                      language === 'ar' ? 'ml-3' : 'mr-3'
                    } text-[#966f2e]`}
                  />
                  <div>
                    <div>
                      {language === 'en'
                        ? 'Employees Absconded'
                        : 'الموظفون الهاربون'}
                    </div>
                    <div className="text-xs text-gray-500">
                      {language === 'en'
                        ? 'List of employees who have absconded'
                        : 'قائمة الموظفين الهاربين'}
                    </div>
                  </div>
                </div>
                <ChevronDown
                  className={`w-5 h-5 text-gray-400 transition-transform ${
                    expandedSections.employeesAbsconded
                      ? 'rotate-180'
                      : ''
                  }`}
                />
              </button>

              {expandedSections.employeesAbsconded && (
                <div className="p-4 bg-gray-50">
                  <div className="flex items-center mb-4">
                    <span className="bg-red-100 text-red-800 text-xs font-medium px-3 py-1 rounded-full">
                      {language === 'en'
                        ? 'Absconded Employees'
                        : 'الموظفون الهاربون'}
                    </span>
                    <span className="ml-3 text-sm text-gray-500">
                      {language === 'en'
                        ? `Total: ${employeesAbsconded.length}`
                        : `الإجمالي: ${employeesAbsconded.length}`}
                    </span>
                  </div>

                  {/* Employees Absconded Table */}
                  <div className="overflow-x-auto">
                    <table className="min-w-full bg-white border border-gold-200 rounded-lg">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {language === 'en' ? 'Name' : 'الاسم'}
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {language === 'en'
                              ? 'EID Number'
                              : 'رقم الهوية'}
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {language === 'en'
                              ? 'Date of Birth'
                              : 'تاريخ الميلاد'}
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {language === 'en' ? 'Gender' : 'الجنس'}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {employeesAbsconded.map((employee, index) => (
                          <tr
                            key={index}
                            className={
                              index % 2 === 0
                                ? 'bg-white'
                                : 'bg-gray-50'
                            }
                          >
                            <td className="px-4 py-2 text-sm">
                              {language === 'en'
                                ? employee.full_name_en
                                : employee.full_name_ar}
                            </td>
                            <td className="px-4 py-2 text-sm">
                              {maskEidNumber(employee.eid_number)}
                            </td>
                            <td className="px-4 py-2 text-sm">
                              {formatDate(employee.date_of_birth)}
                            </td>
                            <td className="px-4 py-2 text-sm">
                              {employee.gender_id === 1
                                ? language === 'en'
                                  ? 'Male'
                                  : 'ذكر'
                                : language === 'en'
                                ? 'Female'
                                : 'أنثى'}
                            </td>
                          </tr>
                        ))}
                        {employeesAbsconded.length === 0 && (
                          <tr>
                            <td
                              colSpan={4}
                              className="px-4 py-4 text-center text-gray-500"
                            >
                              {language === 'en'
                                ? 'No absconded employees data available'
                                : 'لا توجد بيانات للموظفين الهاربين'}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            {/* Owner Details */}
            <div>
              <button
                className="w-full flex items-center justify-between p-4 text-left"
                onClick={() => toggleSection('ownerDetails')}
              >
                <div className="flex items-center">
                  <User className="h-5 w-5 text-[#b89256] mr-3" />
                  <div>
                    <div>
                      {language === 'en'
                        ? 'Owner Details'
                        : 'تفاصيل المالك'}
                    </div>
                    <div className="text-xs text-gray-500">
                      {language === 'en'
                        ? 'Information about establishment owners'
                        : 'معلومات عن مالكي المنشأة'}
                    </div>
                  </div>
                </div>
                <ChevronDown
                  className={`h-5 w-5 text-gray-400 transition-transform ${
                    expandedSections.ownerDetails ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {expandedSections.ownerDetails && (
                <div className="p-4 bg-gray-50">
                  <div className="space-y-6">
                    <div className="flex items-start">
                      <div className="w-12 h-12 bg-gray-200 rounded-full flex-shrink-0"></div>
                      <div className="ml-4">
                        <p className="font-medium">
                          {language === 'en'
                            ? 'Mohammed Al Farsi'
                            : 'محمد الفارسي'}
                        </p>

                        <div className="grid grid-cols-2 gap-4 mt-2">
                          <div>
                            <p className="text-sm text-gray-500">
                              {language === 'en'
                                ? 'Nationality'
                                : 'الجنسية'}
                            </p>
                            <p className="font-medium">
                              {language === 'en' ? 'UAE' : 'الإمارات'}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">
                              {language === 'en'
                                ? 'Ownership'
                                : 'الملكية'}
                            </p>
                            <p className="font-medium">75%</p>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4 mt-2">
                          <div>
                            <p className="text-sm text-gray-500">
                              {language === 'en'
                                ? 'ID Number'
                                : 'رقم الهوية'}
                            </p>
                            <p className="font-medium">
                              784-1980-1234567-1
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">
                              {language === 'en'
                                ? 'Contact'
                                : 'الاتصال'}
                            </p>
                            <p className="font-medium">
                              +971 50 123 4567
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <div className="w-12 h-12 bg-gray-200 rounded-full flex-shrink-0"></div>
                      <div className="ml-4">
                        <p className="font-medium">
                          {language === 'en'
                            ? 'Ahmed Hassan'
                            : 'أحمد حسن'}
                        </p>

                        <div className="grid grid-cols-2 gap-4 mt-2">
                          <div>
                            <p className="text-sm text-gray-500">
                              {language === 'en'
                                ? 'Nationality'
                                : 'الجنسية'}
                            </p>
                            <p className="font-medium">
                              {language === 'en' ? 'UAE' : 'الإمارات'}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">
                              {language === 'en'
                                ? 'Ownership'
                                : 'الملكية'}
                            </p>
                            <p className="font-medium">25%</p>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4 mt-2">
                          <div>
                            <p className="text-sm text-gray-500">
                              {language === 'en'
                                ? 'ID Number'
                                : 'رقم الهوية'}
                            </p>
                            <p className="font-medium">
                              784-1985-7654321-2
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">
                              {language === 'en'
                                ? 'Contact'
                                : 'الاتصال'}
                            </p>
                            <p className="font-medium">
                              +971 55 987 6543
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Workers Details */}
            <div>
              <button
                className="w-full flex items-center justify-between p-4 text-left"
                onClick={() => toggleSection('workersDetails')}
              >
                <div className="flex items-center">
                  <User className="h-5 w-5 text-[#b89256] mr-3" />
                  <div>
                    <div>
                      {language === 'en'
                        ? 'Workers Details'
                        : 'تفاصيل العمال'}
                    </div>
                    <div className="text-xs text-gray-500">
                      {language === 'en'
                        ? 'Employee statistics and sample profiles'
                        : 'إحصائيات الموظفين وعينات من الملفات الشخصية'}
                    </div>
                  </div>
                </div>
                <ChevronDown
                  className={`h-5 w-5 text-gray-400 transition-transform ${
                    expandedSections.workersDetails
                      ? 'rotate-180'
                      : ''
                  }`}
                />
              </button>

              {expandedSections.workersDetails && (
                <div className="p-4 bg-gray-50">
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="bg-blue-50 p-3 rounded-lg text-center">
                      <p className="text-3xl font-bold text-[#b89256]">
                        45
                      </p>
                      <p className="text-sm text-[#b89256]">
                        {language === 'en'
                          ? 'Total Workers'
                          : 'إجمالي العمال'}
                      </p>
                    </div>
                    <div className="bg-indigo-50 p-3 rounded-lg text-center">
                      <p className="text-3xl font-bold text-[#b89256]">
                        32
                      </p>
                      <p className="text-sm text-[#b89256]">
                        {language === 'en' ? 'Male' : 'ذكر'}
                      </p>
                    </div>
                    <div className="bg-purple-50 p-3 rounded-lg text-center">
                      <p className="text-3xl font-bold text-[#b89256]">
                        13
                      </p>
                      <p className="text-sm text-[#b89256]">
                        {language === 'en' ? 'Female' : 'أنثى'}
                      </p>
                    </div>
                  </div>

                  <p className="text-sm font-medium mb-3">
                    {language === 'en'
                      ? 'Sample Workers'
                      : 'عينة من العمال'}
                  </p>
                  <div className="space-y-4">
                    <div className="bg-white p-3 rounded-lg">
                      <div className="flex items-start">
                        <div className="w-10 h-10 bg-gray-200 rounded-full flex-shrink-0"></div>
                        <div className="ml-3 flex-grow">
                          <div className="flex justify-between">
                            <p className="font-medium">
                              {language === 'en'
                                ? 'John Smith'
                                : 'جون سميث'}
                            </p>
                            <span className="bg-gray-100 text-gray-800 text-xs font-medium px-2 py-0.5 rounded">
                              {language === 'en'
                                ? 'UK'
                                : 'المملكة المتحدة'}
                            </span>
                          </div>
                          <div className="grid grid-cols-2 gap-2 mt-1">
                            <div>
                              <p className="text-xs text-gray-500">
                                {language === 'en'
                                  ? 'Position'
                                  : 'المنصب'}
                              </p>
                              <p className="text-sm">
                                {language === 'en'
                                  ? 'Manager'
                                  : 'مدير'}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">
                                {language === 'en'
                                  ? 'Join Date'
                                  : 'تاريخ الانضمام'}
                              </p>
                              <p className="text-sm">2020-03-15</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white p-3 rounded-lg">
                      <div className="flex items-start">
                        <div className="w-10 h-10 bg-gray-200 rounded-full flex-shrink-0"></div>
                        <div className="ml-3 flex-grow">
                          <div className="flex justify-between">
                            <p className="font-medium">
                              {language === 'en'
                                ? 'Fatima Ahmed'
                                : 'فاطمة أحمد'}
                            </p>
                            <span className="bg-gray-100 text-gray-800 text-xs font-medium px-2 py-0.5 rounded">
                              {language === 'en' ? 'Egypt' : 'مصر'}
                            </span>
                          </div>
                          <div className="grid grid-cols-2 gap-2 mt-1">
                            <div>
                              <p className="text-xs text-gray-500">
                                {language === 'en'
                                  ? 'Position'
                                  : 'المنصب'}
                              </p>
                              <p className="text-sm">
                                {language === 'en'
                                  ? 'Supervisor'
                                  : 'مشرف'}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">
                                {language === 'en'
                                  ? 'Join Date'
                                  : 'تاريخ الانضمام'}
                              </p>
                              <p className="text-sm">2021-06-10</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white p-3 rounded-lg">
                      <div className="flex items-start">
                        <div className="w-10 h-10 bg-gray-200 rounded-full flex-shrink-0"></div>
                        <div className="ml-3 flex-grow">
                          <div className="flex justify-between">
                            <p className="font-medium">
                              {language === 'en'
                                ? 'Raj Patel'
                                : 'راج باتيل'}
                            </p>
                            <span className="bg-gray-100 text-gray-800 text-xs font-medium px-2 py-0.5 rounded">
                              {language === 'en' ? 'India' : 'الهند'}
                            </span>
                          </div>
                          <div className="grid grid-cols-2 gap-2 mt-1">
                            <div>
                              <p className="text-xs text-gray-500">
                                {language === 'en'
                                  ? 'Position'
                                  : 'المنصب'}
                              </p>
                              <p className="text-sm">
                                {language === 'en'
                                  ? 'Technician'
                                  : 'فني'}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">
                                {language === 'en'
                                  ? 'Join Date'
                                  : 'تاريخ الانضمام'}
                              </p>
                              <p className="text-sm">2019-11-22</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Nationalities and Professions */}
            <div>
              <button
                className="w-full flex items-center justify-between p-4 text-left"
                onClick={() => toggleSection('nationalities')}
              >
                <div className="flex items-center">
                  <Globe className="h-5 w-5 text-[#b89256] mr-3" />
                  <div>
                    <div>
                      {language === 'en'
                        ? 'Nationalities and Professions'
                        : 'الجنسيات والمهن'}
                    </div>
                    <div className="text-xs text-gray-500">
                      {language === 'en'
                        ? 'Workforce distribution by nationality and quota'
                        : 'توزيع القوى العاملة حسب الجنسية والحصص'}
                    </div>
                  </div>
                </div>
                <ChevronDown
                  className={`h-5 w-5 text-gray-400 transition-transform ${
                    expandedSections.nationalities ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {expandedSections.nationalities && (
                <div className="p-4 bg-gray-50">
                  <div className="space-y-3">
                    <div className="bg-white p-3 rounded-lg">
                      <div className="flex items-center mb-2">
                        <span className="mr-2">🇮🇳</span>
                        <span className="font-medium">
                          {language === 'en' ? 'India' : 'الهند'}
                        </span>
                      </div>
                      <div className="flex justify-between mb-1">
                        <div className="flex space-x-4">
                          <div>
                            <span className="text-xs text-gray-500">
                              {language === 'en' ? 'Count' : 'العدد'}
                            </span>
                            <p className="font-medium">18</p>
                          </div>
                          <div>
                            <span className="text-xs text-gray-500">
                              {language === 'en' ? 'Quota' : 'الحصة'}
                            </span>
                            <p className="font-medium">25</p>
                          </div>
                        </div>
                        <div className="w-24 flex items-center">
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-blue-600 h-2 rounded-full"
                              style={{ width: '72%' }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="bg-white p-3 rounded-lg">
                      <div className="flex items-center mb-2">
                        <span className="mr-2">🇵🇭</span>
                        <span className="font-medium">
                          {language === 'en'
                            ? 'Philippines'
                            : 'الفلبين'}
                        </span>
                      </div>
                      <div className="flex justify-between mb-1">
                        <div className="flex space-x-4">
                          <div>
                            <span className="text-xs text-gray-500">
                              {language === 'en' ? 'Count' : 'العدد'}
                            </span>
                            <p className="font-medium">10</p>
                          </div>
                          <div>
                            <span className="text-xs text-gray-500">
                              {language === 'en' ? 'Quota' : 'الحصة'}
                            </span>
                            <p className="font-medium">15</p>
                          </div>
                        </div>
                        <div className="w-24 flex items-center">
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-blue-600 h-2 rounded-full"
                              style={{ width: '67%' }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white p-3 rounded-lg">
                      <div className="flex items-center mb-2">
                        <span className="mr-2">🇵🇰</span>
                        <span className="font-medium">
                          {language === 'en' ? 'Pakistan' : 'باكستان'}
                        </span>
                      </div>
                      <div className="flex justify-between mb-1">
                        <div className="flex space-x-4">
                          <div>
                            <span className="text-xs text-gray-500">
                              {language === 'en' ? 'Count' : 'العدد'}
                            </span>
                            <p className="font-medium">7</p>
                          </div>
                          <div>
                            <span className="text-xs text-gray-500">
                              {language === 'en' ? 'Quota' : 'الحصة'}
                            </span>
                            <p className="font-medium">10</p>
                          </div>
                        </div>
                        <div className="w-24 flex items-center">
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-blue-600 h-2 rounded-full"
                              style={{ width: '70%' }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Taqyeem Details */}
            <div>
              <button
                className="w-full flex items-center justify-between p-4 text-left"
                onClick={() => toggleSection('taqyeemDetails')}
              >
                <div className="flex items-center">
                  <Award className="h-5 w-5 text-[#b89256] mr-3" />
                  <div>
                    <div>
                      {language === 'en'
                        ? 'Taqyeem Details'
                        : 'تفاصيل تقييم'}
                    </div>
                    <div className="text-xs text-gray-500">
                      {language === 'en'
                        ? 'Ratings, compliance scores and working conditions'
                        : 'التقييمات ودرجات الامتثال وظروف العمل'}
                    </div>
                  </div>
                </div>
                <ChevronDown
                  className={`h-5 w-5 text-gray-400 transition-transform ${
                    expandedSections.taqyeemDetails
                      ? 'rotate-180'
                      : ''
                  }`}
                />
              </button>

              {expandedSections.taqyeemDetails && (
                <div className="p-4 bg-gray-50">
                  <div className="flex items-center mb-3">
                    <div className="flex text-amber-400">
                      <Star className="h-5 w-5 fill-current" />
                      <Star className="h-5 w-5 fill-current" />
                      <Star className="h-5 w-5 fill-current" />
                      <Star className="h-5 w-5 fill-current" />
                      <Star className="h-5 w-5 text-gray-300" />
                    </div>
                    <span className="ml-2 font-bold text-lg">
                      4.5
                    </span>
                    <span className="ml-1 text-gray-500">
                      {language === 'en' ? 'Rating' : 'تقييم'}
                    </span>
                  </div>

                  <div className="mb-4">
                    <div className="flex justify-between mb-1">
                      <span className="text-sm text-gray-500">
                        {language === 'en'
                          ? 'Compliance Score'
                          : 'درجة الامتثال'}
                      </span>
                      <span className="font-medium">92%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                      <div
                        className="bg-[#b89256] h-2.5 rounded-full"
                        style={{ width: '92%' }}
                      ></div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-sm text-gray-500">
                        {language === 'en'
                          ? 'Last Inspection'
                          : 'آخر تفتيش'}
                      </p>
                      <p className="font-medium">2023-01-15</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">
                        {language === 'en'
                          ? 'Opening Hours'
                          : 'ساعات العمل'}
                      </p>
                      <p className="font-medium">
                        {language === 'en'
                          ? '10:00 AM - 10:00 PM'
                          : '10:00 ص - 10:00 م'}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-sm text-gray-500">
                        {language === 'en'
                          ? 'Weekend Days'
                          : 'أيام العطلة الأسبوعية'}
                      </p>
                      <p className="font-medium">
                        {language === 'en'
                          ? 'Friday (Half day), Saturday'
                          : 'الجمعة (نصف يوم)، السبت'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">
                        {language === 'en'
                          ? 'Annual Leave'
                          : 'الإجازة السنوية'}
                      </p>
                      <p className="font-medium">
                        30 {language === 'en' ? 'days' : 'يوم'}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">
                        {language === 'en'
                          ? 'Sick Leave'
                          : 'إجازة مرضية'}
                      </p>
                      <p className="font-medium">
                        15 {language === 'en' ? 'days' : 'يوم'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">
                        {language === 'en'
                          ? 'Maternity Leave'
                          : 'إجازة أمومة'}
                      </p>
                      <p className="font-medium">
                        45 {language === 'en' ? 'days' : 'يوم'}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Contact Details */}
            <div>
              <button
                className="w-full flex items-center justify-between p-4 text-left"
                onClick={() => toggleSection('contactDetails')}
              >
                <div className="flex items-center">
                  <Phone className="h-5 w-5 text-[#b89256] mr-3" />
                  <div>
                    <div>
                      {language === 'en'
                        ? 'Contact Details'
                        : 'تفاصيل الاتصال'}
                    </div>
                    <div className="text-xs text-gray-500">
                      {language === 'en'
                        ? 'Address, phone, email and working hours'
                        : 'العنوان والهاتف والبريد الإلكتروني وساعات العمل'}
                    </div>
                  </div>
                </div>
                <ChevronDown
                  className={`h-5 w-5 text-gray-400 transition-transform ${
                    expandedSections.contactDetails
                      ? 'rotate-180'
                      : ''
                  }`}
                />
              </button>

              {expandedSections.contactDetails && (
                <div className="p-4 bg-gray-50">
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-sm text-gray-500">
                        {language === 'en' ? 'Address' : 'العنوان'}
                      </p>
                      <p className="font-medium">
                        {language === 'en'
                          ? 'Sheikh Zayed Road, Downtown Dubai'
                          : 'شارع الشيخ زايد، وسط مدينة دبي'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">
                        {language === 'en'
                          ? 'P.O. Box'
                          : 'صندوق البريد'}
                      </p>
                      <p className="font-medium">
                        {language === 'en'
                          ? 'P.O. Box 123456'
                          : 'صندوق بريد 123456'}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-sm text-gray-500">
                        {language === 'en' ? 'Phone' : 'الهاتف'}
                      </p>
                      <p className="font-medium">+971 4 123 4567</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">
                        {language === 'en'
                          ? 'Email'
                          : 'البريد الإلكتروني'}
                      </p>
                      <p className="font-medium">
                        info@dubaimall.com
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">
                        {language === 'en'
                          ? 'Website'
                          : 'الموقع الإلكتروني'}
                      </p>
                      <p className="font-medium">www.dubaimall.com</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">
                        {language === 'en'
                          ? 'Working Hours'
                          : 'ساعات العمل'}
                      </p>
                      <p className="font-medium">
                        {language === 'en'
                          ? '10:00 AM - 10:00 PM'
                          : '10:00 ص - 10:00 م'}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Reasons for Risk Level */}
            <div>
              <button
                className="w-full flex items-center justify-between p-4 text-left"
                onClick={() => toggleSection('riskReasons')}
              >
                <div className="flex items-center">
                  <AlertTriangle className="h-5 w-5 text-[#b89256] mr-3" />
                  <div>
                    <div>
                      {language === 'en'
                        ? 'Reasons for Risk Level'
                        : 'أسباب مستوى المخاطر'}
                    </div>
                    <div className="text-xs text-gray-500">
                      {language === 'en'
                        ? 'Risk factors and monitoring recommendations'
                        : 'عوامل الخطر وتوصيات المراقبة'}
                    </div>
                  </div>
                </div>
                <ChevronDown
                  className={`h-5 w-5 text-gray-400 transition-transform ${
                    expandedSections.riskReasons ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {expandedSections.riskReasons && (
                <div className="p-4 bg-gray-50">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-red-500 text-white rounded-lg flex items-center justify-center font-bold">
                      F
                    </div>
                    <div className="ml-3">
                      <p className="font-medium">
                        {language === 'en'
                          ? 'High Risk Establishment'
                          : 'منشأة عالية المخاطر'}
                      </p>
                      <p className="text-sm text-gray-500">
                        {language === 'en'
                          ? 'Requires frequent monitoring'
                          : 'تتطلب مراقبة متكررة'}
                      </p>
                    </div>
                  </div>

                  <p className="font-medium mb-2">
                    {language === 'en'
                      ? 'Risk Factors'
                      : 'عوامل الخطر'}
                  </p>
                  <ul className="space-y-2 mb-4">
                    <li className="flex items-center">
                      <AlertTriangle className="h-4 w-4 text-amber-500 mr-2" />
                      <span>
                        {language === 'en'
                          ? 'Previous safety violations in the last 6 months'
                          : 'مخالفات سلامة سابقة في الأشهر الستة الماضية'}
                      </span>
                    </li>
                    <li className="flex items-center">
                      <AlertTriangle className="h-4 w-4 text-amber-500 mr-2" />
                      <span>
                        {language === 'en'
                          ? 'High employee turnover rate'
                          : 'معدل دوران عالٍ للموظفين'}
                      </span>
                    </li>
                    <li className="flex items-center">
                      <AlertTriangle className="h-4 w-4 text-amber-500 mr-2" />
                      <span>
                        {language === 'en'
                          ? 'Delayed salary payments in the past year'
                          : 'تأخير دفع الرواتب في العام الماضي'}
                      </span>
                    </li>
                    <li className="flex items-center">
                      <AlertTriangle className="h-4 w-4 text-amber-500 mr-2" />
                      <span>
                        {language === 'en'
                          ? 'Previous fire safety non-compliance'
                          : 'عدم الامتثال السابق لسلامة الحرائق'}
                      </span>
                    </li>
                  </ul>

                  <p className="font-medium mb-2">
                    {language === 'en'
                      ? 'Recommendations'
                      : 'التوصيات'}
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-center">
                      <Check className="h-4 w-4 text-green-500 mr-2" />
                      <span>
                        {language === 'en'
                          ? 'Increase frequency of safety inspections'
                          : 'زيادة وتيرة عمليات تفتيش السلامة'}
                      </span>
                    </li>
                    <li className="flex items-center">
                      <Check className="h-4 w-4 text-green-500 mr-2" />
                      <span>
                        {language === 'en'
                          ? 'Monitor WPS compliance closely'
                          : 'مراقبة الامتثال لنظام حماية الأجور عن كثب'}
                      </span>
                    </li>
                    <li className="flex items-center">
                      <Check className="h-4 w-4 text-green-500 mr-2" />
                      <span>
                        {language === 'en'
                          ? 'Follow up on previous violation remediation'
                          : 'متابعة معالجة المخالفات السابقة'}
                      </span>
                    </li>
                  </ul>
                </div>
              )}
            </div>

            {/* Previous Visits */}
            <div>
              <button
                className="w-full flex items-center justify-between p-4 text-left"
                onClick={() => toggleSection('previousVisits')}
              >
                <div className="flex items-center">
                  <Clock className="h-5 w-5 text-[#b89256] mr-3" />
                  <div>
                    <div>
                      {language === 'en'
                        ? 'Previous Visits'
                        : 'الزيارات السابقة'}
                    </div>
                    <div className="text-xs text-gray-500">
                      {language === 'en'
                        ? 'History of inspections and compliance status'
                        : 'تاريخ عمليات التفتيش وحالة الامتثال'}
                    </div>
                  </div>
                </div>
                <ChevronDown
                  className={`h-5 w-5 text-gray-400 transition-transform ${
                    expandedSections.previousVisits
                      ? 'rotate-180'
                      : ''
                  }`}
                />
              </button>

              {expandedSections.previousVisits && (
                <div className="p-4 bg-gray-50">
                  <div className="space-y-4">
                    <div className="bg-white p-3 rounded-lg">
                      <div className="flex justify-between items-center mb-2">
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 text-gray-500 mr-2" />
                          <span className="font-medium">
                            2023-01-15
                          </span>
                        </div>
                        <span className="bg-amber-100 text-amber-800 text-xs font-medium px-2 py-0.5 rounded">
                          {language === 'en'
                            ? 'Minor Violations'
                            : 'مخالفات بسيطة'}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 mb-2">
                        <div>
                          <p className="text-xs text-gray-500">
                            {language === 'en'
                              ? 'Inspector'
                              : 'المفتش'}
                          </p>
                          <p className="text-sm">
                            {language === 'en'
                              ? 'Ahmed Mohammed'
                              : 'أحمد محمد'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">
                            {language === 'en' ? 'Type' : 'النوع'}
                          </p>
                          <p className="text-sm">
                            {language === 'en'
                              ? 'Safety Inspection'
                              : 'تفتيش السلامة'}
                          </p>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <p className="text-xs text-gray-500">
                            {language === 'en'
                              ? 'Violations'
                              : 'المخالفات'}
                          </p>
                          <p className="text-sm">3</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">
                            {language === 'en'
                              ? 'Follow-up'
                              : 'المتابعة'}
                          </p>
                          <p className="text-sm">
                            {language === 'en' ? 'Required' : 'مطلوب'}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white p-3 rounded-lg">
                      <div className="flex justify-between items-center mb-2">
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 text-gray-500 mr-2" />
                          <span className="font-medium">
                            2022-07-22
                          </span>
                        </div>
                        <span className="bg-green-100 text-green-800 text-xs font-medium px-2 py-0.5 rounded">
                          {language === 'en' ? 'Compliant' : 'ملتزم'}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 mb-2">
                        <div>
                          <p className="text-xs text-gray-500">
                            {language === 'en'
                              ? 'Inspector'
                              : 'المفتش'}
                          </p>
                          <p className="text-sm">
                            {language === 'en'
                              ? 'Sara Al Maktoum'
                              : 'سارة المكتوم'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">
                            {language === 'en' ? 'Type' : 'النوع'}
                          </p>
                          <p className="text-sm">
                            {language === 'en'
                              ? 'Fire Safety Check'
                              : 'فحص سلامة الحريق'}
                          </p>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <p className="text-xs text-gray-500">
                            {language === 'en'
                              ? 'Violations'
                              : 'المخالفات'}
                          </p>
                          <p className="text-sm">0</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">
                            {language === 'en'
                              ? 'Follow-up'
                              : 'المتابعة'}
                          </p>
                          <p className="text-sm">
                            {language === 'en'
                              ? 'Not Required'
                              : 'غير مطلوب'}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white p-3 rounded-lg">
                      <div className="flex justify-between items-center mb-2">
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 text-gray-500 mr-2" />
                          <span className="font-medium">
                            2022-03-10
                          </span>
                        </div>
                        <span className="bg-red-100 text-red-800 text-xs font-medium px-2 py-0.5 rounded">
                          {language === 'en'
                            ? 'Major Violations'
                            : 'مخالفات كبيرة'}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 mb-2">
                        <div>
                          <p className="text-xs text-gray-500">
                            {language === 'en'
                              ? 'Inspector'
                              : 'المفتش'}
                          </p>
                          <p className="text-sm">
                            {language === 'en'
                              ? 'Khalid Al Zaabi'
                              : 'خالد الزعابي'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">
                            {language === 'en' ? 'Type' : 'النوع'}
                          </p>
                          <p className="text-sm">
                            {language === 'en'
                              ? 'Structural Inspection'
                              : 'تفتيش هيكلي'}
                          </p>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <p className="text-xs text-gray-500">
                            {language === 'en'
                              ? 'Violations'
                              : 'المخالفات'}
                          </p>
                          <p className="text-sm">5</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">
                            {language === 'en'
                              ? 'Follow-up'
                              : 'المتابعة'}
                          </p>
                          <p className="text-sm">
                            {language === 'en' ? 'Required' : 'مطلوب'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
    </div>
  );
};

export default EstablishmentAccordionPage;
